import type { CalibNormMap } from '../../../domain/RemoteDisplayState.ts';
import type { RemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import type { TrailPoint } from '../../../domain/GameState.ts';

export const LIVE_BONES: [string, string][] = [
  ['LEFT_SHOULDER', 'RIGHT_SHOULDER'],
  ['LEFT_SHOULDER', 'LEFT_ELBOW'],
  ['LEFT_ELBOW', 'LEFT_WRIST'],
  ['RIGHT_SHOULDER', 'RIGHT_ELBOW'],
  ['RIGHT_ELBOW', 'RIGHT_WRIST'],
  ['LEFT_SHOULDER', 'LEFT_HIP'],
  ['RIGHT_SHOULDER', 'RIGHT_HIP'],
  ['LEFT_HIP', 'RIGHT_HIP'],
  ['LEFT_HIP', 'LEFT_KNEE'],
  ['LEFT_KNEE', 'LEFT_ANKLE'],
  ['RIGHT_HIP', 'RIGHT_KNEE'],
  ['RIGHT_KNEE', 'RIGHT_ANKLE'],
  ['LEFT_SHOULDER', 'RIGHT_SHOULDER'],
];

export function clipNormSegmentToGameRect(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  lo: number,
  hi: number,
): { x0: number; y0: number; x1: number; y1: number } | null {
  if (
    !Number.isFinite(x0) ||
    !Number.isFinite(y0) ||
    !Number.isFinite(x1) ||
    !Number.isFinite(y1)
  ) {
    return null;
  }
  let t0 = 0;
  let t1 = 1;
  const dx = x1 - x0;
  const dy = y1 - y0;

  const clip = (p: number, q: number): boolean => {
    if (Math.abs(p) < 1e-14) {
      return q >= 0;
    }
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
    return true;
  };

  if (!clip(-dx, x0 - lo)) return null;
  if (!clip(dx, hi - x0)) return null;
  if (!clip(-dy, y0 - lo)) return null;
  if (!clip(dy, hi - y0)) return null;
  if (t0 > t1) return null;

  return {
    x0: x0 + t0 * dx,
    y0: y0 + t0 * dy,
    x1: x0 + t1 * dx,
    y1: y0 + t1 * dy,
  };
}

export function drawLiveSkeletonPts(
  ctx: CanvasRenderingContext2D,
  pts: CalibNormMap,
  layout: RemoteViewLayout,
  playing: boolean,
  cr: number,
  cg: number,
  cb: number,
  useLighterCanvasEffects: boolean,
): void {
  if (pts.size === 0) return;
  const { offsetX, offsetY, gameWidth, gameHeight } = layout;
  const alpha = playing ? 0.3 : 0.9;
  const liveW = Math.max(gameHeight * (playing ? 0.01 : 0.018), playing ? 3 : 5);
  const gLo = -1e-6;
  const gHi = 1 + 1e-6;
  const normOk = (nx: number, ny: number): boolean => {
    if (!Number.isFinite(nx) || !Number.isFinite(ny)) return false;
    if (!playing && Math.abs(nx) < 1e-5 && Math.abs(ny) < 1e-5) {
      return false;
    }
    return nx >= gLo && nx <= gHi && ny >= gLo && ny <= gHi;
  };

  const isPlaceholderJoint = (nx: number, ny: number): boolean =>
    !playing && Math.abs(nx) < 1e-5 && Math.abs(ny) < 1e-5;

  const light = useLighterCanvasEffects;
  let boneShadowBlur: number;
  if (light) {
    boneShadowBlur = playing ? 4 : 10;
  } else {
    boneShadowBlur = playing ? 10 : 22;
  }
  ctx.save();
  ctx.strokeStyle = `rgba(${cr},${cg},${cb},${alpha})`;
  ctx.lineWidth = liveW;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.shadowBlur = boneShadowBlur;
  ctx.shadowColor = `rgba(${cr},${cg},${cb},${alpha * 0.8})`;

  for (const [a, b] of LIVE_BONES) {
    const pa = pts.get(a as never);
    const pb = pts.get(b as never);
    if (!pa || !pb) continue;
    if (isPlaceholderJoint(pa.nx, pa.ny) || isPlaceholderJoint(pb.nx, pb.ny)) {
      continue;
    }
    const seg = clipNormSegmentToGameRect(pa.nx, pa.ny, pb.nx, pb.ny, gLo, gHi);
    if (!seg) continue;
    ctx.beginPath();
    ctx.moveTo(offsetX + seg.x0 * gameWidth, offsetY + seg.y0 * gameHeight);
    ctx.lineTo(offsetX + seg.x1 * gameWidth, offsetY + seg.y1 * gameHeight);
    ctx.stroke();
  }

  const le = pts.get('LEFT_EYE' as never);
  const re = pts.get('RIGHT_EYE' as never);
  if (le && re && normOk(le.nx, le.ny) && normOk(re.nx, re.ny)) {
    const hx = ((le.nx + re.nx) / 2) * gameWidth + offsetX;
    const hy = ((le.ny + re.ny) / 2) * gameHeight + offsetY;
    const hr = Math.max(Math.abs(le.nx - re.nx) * gameWidth * 1.4, gameHeight * 0.017);
    ctx.beginPath();
    ctx.arc(hx, hy, hr, 0, Math.PI * 2);
    ctx.stroke();
  }

  const dotR = playing ? liveW * 1.4 : liveW * 2.2;
  ctx.fillStyle = `rgba(${cr},${cg},${cb},${Math.min(1, alpha + 0.05)})`;
  let dotShadowBlur: number;
  if (light) {
    dotShadowBlur = playing ? 2 : 6;
  } else {
    dotShadowBlur = playing ? 6 : 16;
  }
  ctx.shadowBlur = dotShadowBlur;
  pts.forEach(({ nx, ny }) => {
    if (!normOk(nx, ny)) return;
    ctx.beginPath();
    ctx.arc(offsetX + nx * gameWidth, offsetY + ny * gameHeight, dotR, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.restore();
}

/** P1 colour: cyan — matches P1 skeleton (80, 220, 255). */
const TRAIL_COLOR_P1 = 'rgba(80,220,255,0.9)';
/** P2 colour: pink — matches P2 skeleton (255, 120, 200). */
const TRAIL_COLOR_P2 = 'rgba(255,120,200,0.9)';
/** Solo / fallback left-hand colour. */
const TRAIL_COLOR_SOLO_LEFT = 'rgba(80,180,255,0.85)';
/** Solo / fallback right-hand colour. */
const TRAIL_COLOR_SOLO_RIGHT = 'rgba(255,160,60,0.85)';

function trailShadowColor(
  key: string,
  p1PlayerId: string | null | undefined,
  p2PlayerId: string | null | undefined,
): string {
  if (p1PlayerId && p2PlayerId) {
    // DUO mode — colour by player
    if (key.startsWith(`${p1PlayerId}_`)) return TRAIL_COLOR_P1;
    if (key.startsWith(`${p2PlayerId}_`)) return TRAIL_COLOR_P2;
    return TRAIL_COLOR_P1; // unexpected key — fall back to P1
  }
  // Solo mode — colour by hand (original behaviour)
  return key.endsWith('_Left') ? TRAIL_COLOR_SOLO_LEFT : TRAIL_COLOR_SOLO_RIGHT;
}

export function drawBladeTrails(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  bladeTrails: Map<string, TrailPoint[]>,
  gameBounds: RemoteViewLayout | undefined,
  useLighterCanvasEffects: boolean,
  p1PlayerId?: string | null,
  p2PlayerId?: string | null,
): void {
  const now = Date.now();
  const W = canvasWidth;
  bladeTrails.forEach((trail, key) => {
    const expireBefore = now - 250;
    const firstValid = trail.findIndex((p) => p.t > expireBefore);
    if (firstValid === -1) return;

    const tip = trail[trail.length - 1];
    if (!tip) return;

    if (gameBounds) {
      const { offsetX, offsetY, gameWidth, gameHeight } = gameBounds;
      if (
        tip.x < offsetX ||
        tip.x > offsetX + gameWidth ||
        tip.y < offsetY ||
        tip.y > offsetY + gameHeight
      ) {
        return;
      }
    }

    const trailColor = trailShadowColor(key, p1PlayerId, p2PlayerId);
    const radius = Math.max(16, W * 0.022);

    // Draw trajectory line through trail points
    const validTrail = trail.slice(firstValid);
    if (validTrail.length >= 2) {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(validTrail[0]!.x, validTrail[0]!.y);
      for (let i = 1; i < validTrail.length; i++) {
        ctx.lineTo(validTrail[i]!.x, validTrail[i]!.y);
      }
      ctx.strokeStyle = trailColor;
      ctx.lineWidth = Math.max(2, W * 0.004);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = 0.75;
      ctx.stroke();
      ctx.restore();
    }

    // Draw cursor circle centered exactly on tip
    ctx.save();
    ctx.beginPath();
    ctx.arc(tip.x, tip.y, radius, 0, Math.PI * 2);
    ctx.fillStyle = trailColor;
    ctx.globalAlpha = 0.85;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = Math.max(2, radius * 0.15);
    ctx.globalAlpha = 1;
    ctx.stroke();
    ctx.restore();
  });
}
